import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { fetchLogisticsInfo } from '../../services/logistics';

const toNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatMoney = (value, prefix) => `${prefix}${toNumber(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LogisticsInfoPage = () => {
  const { t } = useTranslation(['logistics']);
  const [methodFilter, setMethodFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  useEffect(() => {
    const loadLogistics = async () => {
      setLoading(true);
      try {
        const params = { page: 1, page_size: 100 };
        if (methodFilter !== 'all') {
          params.method = methodFilter;
        }
        const response = await fetchLogisticsInfo(params);
        const results = Array.isArray(response?.results) ? response.results : [];
        setRows(results);
      } catch (error) {
        setRows([]);
        setSnackbar({
          open: true,
          message: t('logistics:messages.failedToLoad'),
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    loadLogistics();
  }, [methodFilter, t]);

  const mappedRows = useMemo(
    () =>
      rows.map((item) => {
        const freightFeeUsd = toNumber(item.freight_fee);
        const dollarRate = toNumber(item.dollar_rate);
        const clearanceFee = toNumber(item.clearance_fee);
        const computedTotal = freightFeeUsd * dollarRate + clearanceFee;
        return {
          ...item,
          computedTotal,
          methodLabel: item.method === 'air' ? t('logistics:shippingMethod.air') : t('logistics:shippingMethod.sea'),
        };
      }),
    [rows, t]
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color="#DC143C">
            {t('logistics:info.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('logistics:info.subtitle')}
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t('logistics:filters.shippingMethod')}</InputLabel>
          <Select
            value={methodFilter}
            label={t('logistics:filters.shippingMethod')}
            onChange={(event) => setMethodFilter(event.target.value)}
          >
            <MenuItem value="all">{t('logistics:filters.allMethods')}</MenuItem>
            <MenuItem value="air">{t('logistics:filters.airFreight')}</MenuItem>
            <MenuItem value="sea">{t('logistics:filters.seaFreight')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Typography variant="body1">{t('logistics:info.loading')}</Typography>
      ) : mappedRows.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1">{t('logistics:info.empty')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {mappedRows.map((item) => (
            <Grid key={item.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    {item.shipping_agent || t('logistics:info.unknownAgent')}
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.method')}:</strong> {item.methodLabel}</Typography>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.terminal')}:</strong> {item.terminal_port || '—'}</Typography>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.transitTime')}:</strong> {item.average_time || '—'}</Typography>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.freightFee')}:</strong> {formatMoney(item.freight_fee, '$')}</Typography>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.clearance')}:</strong> {formatMoney(item.clearance_fee, '₦')}</Typography>
                    <Typography variant="body2"><strong>{t('logistics:info.labels.exchangeRate')}:</strong> {formatMoney(item.dollar_rate, '₦')}</Typography>
                    <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ pt: 0.5 }}>
                      {t('logistics:info.labels.total')}: {formatMoney(item.computedTotal, '₦')}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LogisticsInfoPage;
